import { sendEmail } from './email';
import { CARRIER_GATEWAYS } from './sms-gateways';
import { supabase, isSupabaseConfigured } from './supabase';

export interface SMSResult {
  success: boolean;
  message: string;
  formattedPhone: string;
  gatewaysUsed?: string[];
  sid?: string;
  error?: string;
}

/**
 * Standardized SMS sending utility with Twilio + Gmail Gateway fallback
 */
export async function sendSMS(
  phoneNumber: string,
  message: string,
  options?: {
    carrier?: string;
    fromNumber?: string;
    userId?: string;
  }
): Promise<SMSResult> {
  const carrier = options?.carrier || 'Unknown';
  const userId = options?.userId;
  const preferredFrom = options?.fromNumber;

  // Step 1: Clean phone number
  const cleanPhone = phoneNumber.replace(/[^\d]/g, '');
  const finalPhone = cleanPhone.length === 10 ? `+1${cleanPhone}` : `+${cleanPhone}`;
  
  if (cleanPhone.length < 10) {
    return {
      success: false,
      message: 'Invalid phone number format.',
      formattedPhone: phoneNumber
    };
  }

  try {
    if (isSupabaseConfigured && supabase && userId) {
      // Step 2: Fetch Agent Preferences for Twilio
      const { data: prefs } = await supabase
        .from('agent_preferences')
        .select('twilio_account_sid, twilio_auth_token, twilio_phone_numbers')
        .eq('user_id', userId)
        .maybeSingle();

      if (prefs?.twilio_account_sid && prefs?.twilio_auth_token) {
        const fromNumber = preferredFrom || (prefs.twilio_phone_numbers?.[0]) || '';
        
        if (fromNumber) {
          console.log(`[SMS-Twilio] Sending to ${finalPhone} from ${fromNumber}`);
          const supabaseUrl = (import.meta as any).env.VITE_SUPABASE_URL;
          const anonKey = (import.meta as any).env.VITE_SUPABASE_ANON_KEY;

          const res = await fetch(`${supabaseUrl}/functions/v1/twilio-sms`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': anonKey,
              'Authorization': `Bearer ${anonKey}`
            },
            body: JSON.stringify({
              to: finalPhone,
              message: message.replace(/^\/+\s*/, '').trim(),
              accountSid: prefs.twilio_account_sid,
              authToken: prefs.twilio_auth_token,
              fromNumber
            })
          });

          const result = await res.json();
          if (res.ok) {
            return {
              success: true,
              message: `✅ SMS delivered via Twilio (${fromNumber})`,
              formattedPhone: cleanPhone,
              sid: result.sid
            };
          } else {
            console.warn('[SMS-Twilio] Failed, falling back to Gmail:', result.error);
          }
        }
      }
    }

  // Step 3: Fallback to Gmail-SMS Gateway
    const clean10 = cleanPhone.slice(-10);
    
    // Improved sanitization for outbound SMS (v15.0)
    const hardenedMessage = message.replace(/^\/+\s*/, '').trim();

    const gateways = CARRIER_GATEWAYS[carrier] || CARRIER_GATEWAYS['Unknown'] || ['tmomail.net', 'vtext.com'];
    
    console.log(`[SMS-Gmail] Fallback sending to ${clean10} (${carrier})`);

    let lastError = '';
    let success = false;
    const successfulGateways: string[] = [];

    for (const gateway of gateways) {
      const to = `${clean10}@${gateway}`;
      try {
        const res = await sendEmail({ 
          to, 
          subject: '', 
          text: hardenedMessage,
          mode: 'sms'
        });
        if (res.success) {
          successfulGateways.push(gateway);
          success = true;
          if (carrier !== 'Unknown') break;
        } else {
          lastError = res.error || 'Gateway error';
        }
      } catch (e: any) {
        lastError = e.message;
      }
      
      if (gateway !== gateways[gateways.length - 1]) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // Step 4: Final Persistence (v15.0)
    // Always attempt to save the outgoing message to the persistent database
    if (isSupabaseConfigured && supabase && userId) {
      try {
        const { error: dbError } = await supabase.from('sms_messages').insert({
          user_id: userId,
          agent_id: userId,
          phone_number: clean10,
          content: hardenedMessage,
          message_body: hardenedMessage,
          direction: 'outbound',
          status: success ? 'sent' : 'failed',
          carrier: carrier !== 'Unknown' ? carrier : (successfulGateways[0] || 'Unknown'),
          error: success ? null : lastError
        });
        if (dbError) console.error('[SMS-DB] Failed to persist outgoing message:', dbError);
        else console.log('[SMS-DB] Outgoing message persisted successfully');
      } catch (e) {
        console.error('[SMS-DB] Persistence error:', e);
      }
    }

    if (success) {
      return {
        success: true,
        message: `✅ SMS delivered via ${successfulGateways.join(', ')}`,
        formattedPhone: clean10,
        gatewaysUsed: successfulGateways
      };
    }
    
    return {
      success: false,
      message: `All gateways failed. ${lastError}`,
      formattedPhone: clean10,
      error: lastError
    };

  } catch (err: any) {
    return {
      success: false,
      message: `System error: ${err.message}`,
      formattedPhone: cleanPhone,
      error: err.message
    };
  }
}

